/**
 * Metriekencatalogus-fundering (M11 / US04K).
 *
 * De catalogus is de bron van waarheid voor metriekcodes, waardetypen, eenheden,
 * standaardclassificatie en bronvoorkeur. Connectors mogen alleen observaties
 * aanleveren voor metrieken die hier gedefinieerd zijn; onbekende codes worden
 * bij normalisatie geweigerd (nooit stilzwijgend opgeslagen).
 */
export const OBSERVATION_VALUE_TYPES = ['NUMBER', 'TEXT', 'BOOLEAN', 'RANGE', 'LIST'];
export const OBSERVATION_CLASSIFICATIONS = ['FACT', 'ESTIMATE', 'INFERENCE'];
export const OBSERVATION_AVAILABILITIES = [
    'AVAILABLE',
    'NOT_AVAILABLE',
    'NOT_APPLICABLE',
    'ERROR',
];
// ─────────────────────────────────────────────────────────────────────────
// Bronfamilies (docs/RELIABILITY_MODEL.md)
// ─────────────────────────────────────────────────────────────────────────
/**
 * Twee routes naar dezelfde onderliggende bron zijn één bronfamilie en tellen
 * dus nooit als onafhankelijke bevestiging (bijv. Google Ads direct en via
 * Windsor.ai).
 */
export const SOURCE_FAMILIES = [
    'WEBSITE',
    'GOOGLE_SEARCH',
    'SE_RANKING',
    'GOOGLE_ADS',
    'SEARCH_CONSOLE',
    'SALESFEED',
    'SOCIAL_PLATFORMS',
    'MANUAL',
];
const CONNECTOR_SOURCE_FAMILY = {
    'CON-01': 'GOOGLE_SEARCH',
    'CON-02': 'SE_RANKING',
    'CON-03': 'GOOGLE_ADS',
    'CON-04': 'SEARCH_CONSOLE',
    'CON-05': 'SALESFEED',
};
/**
 * Bepaalt de bronfamilie van een connector. Een aggregator (CON-07 Windsor.ai)
 * volgt de onderliggende bron en moet die expliciet meegeven; OpenAI (CON-06)
 * heeft geen zelfstandige bronkwaliteit en levert dus nooit een bronfamilie.
 */
export function resolveSourceFamily(connectorCode, underlyingFamily) {
    if (connectorCode === 'CON-07')
        return underlyingFamily ?? null;
    if (connectorCode === 'CON-06')
        return null;
    return CONNECTOR_SOURCE_FAMILY[connectorCode] ?? null;
}
/** Alleen verschillende bronfamilies tellen als onafhankelijke bevestiging. */
export function areIndependentSources(a, b) {
    if (a === null || b === null)
        return false;
    return a !== b;
}
/**
 * Funderingsset: metrieken voor de reeds gebouwde websiteconnector (M6) en de
 * goedgekeurde connectors SE Ranking (CON-02) en Search Console (CON-04), plus
 * de positioneringsmetriek met bandbreedte (M17-voorbereiding). Uitbreiding
 * gebeurt per vergelijkingsmilestone (M13–M17); codes zijn stabiel.
 */
export const METRIC_CATALOG = [
    // Website (M6)
    {
        code: 'website.reachable',
        nameNl: 'Website bereikbaar',
        category: 'WEBSITE',
        valueType: 'BOOLEAN',
        defaultClassification: 'FACT',
        direction: 'NEUTRAL',
        sourcePreference: ['WEBSITE'],
        freshnessWindowDays: 30,
        descriptionNl: 'Of de publieke website bij het ophalen bereikbaar was.',
    },
    {
        code: 'website.detected_services',
        nameNl: 'Diensten volgens eigen website',
        category: 'WEBSITE',
        valueType: 'LIST',
        defaultClassification: 'INFERENCE',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 180,
        descriptionNl: 'Diensten zoals afgeleid uit de publieke website; eigen claims, niet onafhankelijk.',
    },
    // SEO (CON-02 SE Ranking)
    {
        code: 'seo.organic_keywords',
        nameNl: 'Organische zoekwoorden',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SE_RANKING', 'SEARCH_CONSOLE'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal zoekwoorden waarop het domein organisch zichtbaar is (providerschatting).',
    },
    {
        code: 'seo.organic_traffic',
        nameNl: 'Geschat organisch verkeer',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'bezoeken/maand',
        defaultClassification: 'ESTIMATE',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Geschat organisch verkeer per maand; voor het eigen bedrijf gaat first-party data voor.',
    },
    {
        code: 'seo.backlinks',
        nameNl: 'Backlinks',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Totaal aantal bekende backlinks naar het domein (providerschatting).',
    },
    {
        code: 'seo.referring_domains',
        nameNl: 'Verwijzende domeinen',
        category: 'SEO',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal unieke domeinen dat naar het domein verwijst (providerschatting).',
    },
    {
        code: 'seo.top_keywords',
        nameNl: 'Belangrijkste organische zoekwoorden',
        category: 'SEO',
        valueType: 'LIST',
        defaultClassification: 'ESTIMATE',
        direction: 'NEUTRAL',
        sourcePreference: ['SE_RANKING', 'SEARCH_CONSOLE'],
        freshnessWindowDays: 90,
        descriptionNl: 'Best presterende organische zoekwoorden van het domein.',
    },
    // Search Console (CON-04, first-party, alleen eigen bedrijf)
    {
        code: 'search_console.clicks',
        nameNl: 'Zoekclicks (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party clicks uit Google Zoeken over de meetperiode; alleen het eigen bedrijf.',
    },
    {
        code: 'search_console.impressions',
        nameNl: 'Zoekvertoningen (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party vertoningen in Google Zoeken over de meetperiode; alleen het eigen bedrijf.',
    },
    {
        code: 'search_console.ctr',
        nameNl: 'Doorklikratio (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'procent',
        defaultClassification: 'FACT',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party CTR (clicks/vertoningen) over de meetperiode; alleen het eigen bedrijf.',
    },
    {
        code: 'search_console.average_position',
        nameNl: 'Gemiddelde positie (eigen site)',
        category: 'SEARCH_CONSOLE',
        valueType: 'NUMBER',
        unit: 'positie',
        defaultClassification: 'FACT',
        direction: 'LOWER_IS_BETTER',
        sourcePreference: ['SEARCH_CONSOLE'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'Gemiddelde organische positie in Google Zoeken; alleen het eigen bedrijf.',
    },
    // SEA (M14): indicaties van advertentie-activiteit uit toegestane externe
    // data (SE Ranking adv-dataset). Eigen Google Ads-feiten volgen pas na
    // goedkeuring van CON-03; die krijgen dan GOOGLE_ADS als voorkeursbron.
    {
        code: 'sea.paid_keywords',
        nameNl: 'Betaalde zoekwoorden (indicatie)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'ESTIMATE',
        direction: 'NEUTRAL',
        sourcePreference: ['GOOGLE_ADS', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Aantal zoekwoorden waarop het domein adverteert volgens externe data; indicatie van SEA-activiteit, geen accountfeit.',
    },
    {
        code: 'sea.paid_traffic',
        nameNl: 'Geschat betaald verkeer (indicatie)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'bezoeken/maand',
        defaultClassification: 'ESTIMATE',
        direction: 'NEUTRAL',
        sourcePreference: ['GOOGLE_ADS', 'SE_RANKING'],
        freshnessWindowDays: 90,
        descriptionNl: 'Geschat verkeer uit betaalde zoekresultaten volgens externe data; indicatie van SEA-activiteit, geen accountfeit.',
    },
    // Google Ads first-party (C03/CON-03): echte accountprestaties van het eigen
    // bedrijf — feiten, uitsluitend voor COMPANY (companyOnly), nooit concurrenten.
    {
        code: 'sea.paid_clicks',
        nameNl: 'Betaalde klikken (eigen account)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['GOOGLE_ADS'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party klikken uit Google Ads over de meetperiode; alleen het eigen account.',
    },
    {
        code: 'sea.paid_impressions',
        nameNl: 'Betaalde vertoningen (eigen account)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'aantal',
        defaultClassification: 'FACT',
        direction: 'HIGHER_IS_BETTER',
        sourcePreference: ['GOOGLE_ADS'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party advertentievertoningen uit Google Ads over de meetperiode; alleen het eigen account.',
    },
    {
        code: 'sea.paid_cost',
        nameNl: 'Advertentiekosten (eigen account)',
        category: 'SEA',
        valueType: 'NUMBER',
        unit: 'EUR',
        defaultClassification: 'FACT',
        direction: 'NEUTRAL',
        sourcePreference: ['GOOGLE_ADS'],
        freshnessWindowDays: 35,
        companyOnly: true,
        descriptionNl: 'First-party advertentiekosten uit Google Ads over de meetperiode (accountvaluta); alleen het eigen account.',
    },
    // Positionering (M17 / US05F): producten/diensten staan al in
    // `website.detected_services`; hieronder de overige positioneringsdimensies,
    // deterministisch afgeleid uit de publieke website per subject. Het zijn eigen
    // claims van de bron — dus classificatie INFERENCE en niet onafhankelijk.
    {
        code: 'positioning.propositions',
        nameNl: 'Kernproposities',
        category: 'POSITIONING',
        valueType: 'LIST',
        defaultClassification: 'INFERENCE',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 180,
        descriptionNl: 'Belangrijkste proposities/USP’s zoals afgeleid uit de publieke website; eigen claims, niet onafhankelijk geverifieerd.',
    },
    {
        code: 'positioning.geographic_focus',
        nameNl: 'Geografische focus',
        category: 'POSITIONING',
        valueType: 'TEXT',
        defaultClassification: 'INFERENCE',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Geografisch werkgebied zoals afgeleid uit de publieke website (bijv. land of regio).',
    },
    {
        code: 'positioning.industry',
        nameNl: 'Branche-indicatie',
        category: 'POSITIONING',
        valueType: 'TEXT',
        defaultClassification: 'INFERENCE',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Branche/sector zoals afgeleid uit de publieke website; indicatief, niet onafhankelijk geverifieerd.',
    },
    // Omzetinschatting: uitsluitend met bandbreedte, bron en schattingslabel.
    {
        code: 'positioning.revenue_estimate',
        nameNl: 'Omzetindicatie',
        category: 'POSITIONING',
        valueType: 'RANGE',
        unit: 'EUR/jaar',
        defaultClassification: 'ESTIMATE',
        direction: 'NEUTRAL',
        sourcePreference: ['MANUAL', 'WEBSITE'],
        freshnessWindowDays: 365,
        descriptionNl: 'Omzetinschatting, uitsluitend als bandbreedte met bron- en schattingslabel.',
    },
];
const CATALOG_BY_CODE = new Map(METRIC_CATALOG.map((m) => [m.code, m]));
export function getMetricDefinition(code) {
    return CATALOG_BY_CODE.get(code);
}
export function isKnownMetric(code) {
    return CATALOG_BY_CODE.has(code);
}
/**
 * Strikte scheiding tussen eigen feiten en concurrentieschattingen (M14):
 * first-party metrieken (`companyOnly`) mogen uitsluitend voor het eigen
 * bedrijf worden opgeslagen, nooit voor een concurrent.
 */
export function isMetricAllowedForSubject(code, subjectType) {
    const def = CATALOG_BY_CODE.get(code);
    if (!def)
        return false;
    if (def.companyOnly)
        return subjectType === 'COMPANY';
    return true;
}
//# sourceMappingURL=metrics.js.map